// 젬 상점 브라우저 클라이언트: /api/iap/* 호출 · 포트원 V2 결제창(SDK를 필요할 때만 불러온다) · 모바일 리디렉션 복귀 처리.
// 재화는 서버 장부가 정본이다: 여기서는 지갑을 메모리에만 두고(localStorage·스냅샷에 쓰지 않는다) 서버 응답으로만 바꾼다.
import type { IapConfigView, IapOrderView, IapWalletView } from './model';
import { errorText, isUserCancel, shopMode, type ShopMode } from './model';
import { isAppChannel } from '../shared/appChannel';
import {
  PLAY_CANCEL_TEXT, PLAY_PENDING_DELAYS, PLAY_PENDING_TEXT, PLAY_RESTORED_TEXT, connectPlay, isPlayCancel, loadPending, loadPlayPrices, playCompleteAction, requestPlayPurchase, restoreGained, restoreTargets, savePending,
  type DigitalGoodsService, type PlayPrice,
} from './play';

/**
 * 포트원 V2 브라우저 SDK(공식 CDN, 결제 때만 동적으로 불러온다). 버전 없는 주소라 포트원이 고쳐 올리면 내용이 바뀌므로 SRI(integrity)를 박을 수 없다 — 대신 CSP(public/_headers, 지금은 Report-Only)로
 * script-src를 cdn.portone.io로 한정하고 frame-src·connect-src·form-action에 포트원·PG 도메인만 허용한다. 강제 전환·SDK 고정 버전 확인은 다음 단계(PRD §10).
 */
export const PORTONE_SDK_URL = 'https://cdn.portone.io/v2/browser-sdk.js';
const TESTER_KEY = 'dungeonhero.iap.tester';
const RETURN_FLAG = 'iap_return';

interface PortoneSdk {
  requestPayment(req: Record<string, unknown>): Promise<{ code?: string; message?: string; paymentId?: string } | undefined>;
}
declare global { interface Window { PortOne?: PortoneSdk } }

export type IapPhase =
  | { kind: 'idle' }
  | { kind: 'busy'; text: string }
  | { kind: 'done'; text: string; src?: 'refund' }
  | { kind: 'error'; text: string; src?: 'refund' };

async function api<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<{ ok: true; data: T } | { ok: false; status: number; code: string }> {
  try {
    const res = await fetch(path, {
      method, credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json().catch(() => null) as (T & { error?: string }) | null;
    if (res.ok && data) return { ok: true, data };
    return { ok: false, status: res.status, code: data?.error ?? 'server' };
  } catch { return { ok: false, status: 0, code: 'network' }; }
}

/** 앱 채널이면 서버에 힌트를 싣는다(서버는 TWA를 판별 못 하므로 정직한 클라이언트용 이중 방어일 뿐) */
function channelHint(): { channel?: 'app' } { return isAppChannel() ? { channel: 'app' } : {}; }

let sdkLoading: Promise<PortoneSdk> | null = null;
function loadSdk(): Promise<PortoneSdk> {
  if (window.PortOne) return Promise.resolve(window.PortOne);
  sdkLoading ??= new Promise<PortoneSdk>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = PORTONE_SDK_URL;
    s.async = true;
    s.onload = () => (window.PortOne ? resolve(window.PortOne) : reject(new Error('sdk')));
    s.onerror = () => { sdkLoading = null; s.remove(); reject(new Error('sdk')); };
    document.head.appendChild(s);
  });
  return sdkLoading;
}

/** ?iap=test 로 한 번 열면 이 탭(세션 저장소)에서 테스트 결제 상점이 보인다. 실결제(live)면 누구나 보인다 */
export function isTester(): boolean {
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('iap') === 'test') sessionStorage.setItem(TESTER_KEY, '1');
    return sessionStorage.getItem(TESTER_KEY) === '1';
  } catch { return false; }
}

export interface Buyer { name: string; phone: string; email: string }

/** 이 지갑 모드에서 결제(또는 환불)된 상품 id들 */
export function boughtIn(orders: readonly IapOrderView[], mode: 'live' | 'test'): Set<string> {
  return new Set(orders.filter((x) => (x.status === 'paid' || x.status === 'refunded') && x.mode === mode).map((x) => x.productId));
}

/** 복원 실패가 일시적인가(같은 페이지 세션에서 다시 시도해도 되는가) */
export function restoreRetryable(status: number, code: string): boolean {
  return status === 0 || status === 401 || status === 403 || status === 429 || status >= 500 || code === 'payment-pending' || code === 'play-unavailable';
}

export class IapClient {
  config: IapConfigView | null = null;
  wallet: IapWalletView | null = null;
  orders: IapOrderView[] = [];
  phase: IapPhase = { kind: 'idle' };
  /** 이 계정이 이미 산 상품(첫 결제 보너스 표시용 — 판정은 서버) */
  bought = new Set<string>();
  /** 화면 다시 그리기 신호(바뀔 때마다 +1) */
  version = 0;
  private readonly listeners: Array<() => void> = [];
  private loaded = false;
  /** 앱에서 Digital Goods 서비스가 열렸는가(Play 설치본일 때만) */
  private playSvc: DigitalGoodsService | null = null;
  /** Play 현지 가격(SKU → 가격). 못 읽으면 비어 있고 카탈로그 원화를 보인다 */
  playPrices = new Map<string, PlayPrice>();
  private viewSent = false;
  /** 이 페이지 세션에서 복원을 이미 시도한 purchaseToken(같은 토큰은 한 번만) */
  private restored = new Set<string>();

  onChange(f: () => void): void { this.listeners.push(f); }
  private bump(): void { this.version++; for (const f of this.listeners) f(); }
  private set(phase: IapPhase): void { this.phase = phase; this.bump(); }

  /** 설정 한 번(503이면 null = 상점 숨김) + 리디렉션 복귀 처리 */
  async init(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    const r = await api<IapConfigView>('GET', '/api/iap/config');
    this.config = r.ok ? r.data : null;
    this.bump();
    if (this.config && isAppChannel() && this.config.play) {
      this.playSvc = await connectPlay();
      if (this.playSvc) this.playPrices = await loadPlayPrices(this.playSvc, this.config.products.filter((p) => p.enabled && p.playSku).map((p) => p.playSku!));
      this.bump();
      // 앱을 켤 때도 한 번 복원한다(상점을 열 때만이 아니라): TWA 결제 브리지(Android Browser Helper)는 obfuscatedAccountId를 Play에 넘기지 않아
      // 결제 뒤 complete 전에 앱이 꺼진 구매를 RTDN이 계정에 잇지 못한다 — 이 계정의 미소비 구매는 앱 실행 때 listPurchases()로 되찾는다(3일 자동 환불 전)
      if (this.playSvc) void this.restorePlay();
    }
    if (this.config) await this.handleReturn();
  }

  /** 지금 상점 탭이 할 일(null = 탭 숨김) */
  mode(): ShopMode | null { return shopMode(this.config, { app: isAppChannel(), tester: isTester(), playSupported: !!this.playSvc }); }
  /** 결제 채널 이름(퍼널 이벤트용) */
  channel(): 'web' | 'play' { return this.mode() === 'play' ? 'play' : 'web'; }

  /** 퍼널 이벤트(노출·제안). 실패는 무시한다 */
  async sendEvent(body: { stage: 'view' | 'offer'; productId?: string; trigger?: 'levelup' | 'job' | 'defeat' | 'shop' }): Promise<void> {
    await api('POST', '/api/iap/event', { ...body, channel: this.channel() });
  }
  /** 상점 탭을 열 때 한 번(이 페이지 세션당) */
  viewOnce(): void { if (this.viewSent || !this.config) return; this.viewSent = true; void this.sendEvent({ stage: 'view' }); }

  async refresh(): Promise<void> {
    if (!this.config) return;
    const [w, o] = await Promise.all([api<{ wallet: IapWalletView }>('GET', '/api/iap/wallet'), api<{ orders: IapOrderView[] }>('GET', '/api/iap/orders')]);
    if (w.ok) this.wallet = w.data.wallet;
    void this.recoverPlay();
    void this.restorePlay();
    if (o.ok) {
      this.orders = o.data.orders;
      // 지금 지갑 모드(실결제/테스트)의 주문만 센다 — 서버의 계정당 1회 제한도 같은 모드 안에서만 센다(테스트 때 산 스타터 팩이 실결제 버튼을 막지 않게)
      this.bought = boughtIn(this.orders, this.config.live ? 'live' : 'test');
    }
    this.bump();
  }

  /** 주문 → 결제창 → 서버 확인(지급). 모바일은 결제창이 페이지를 떠났다가 ?iap_return=1&paymentId=…로 돌아온다 */
  async buy(productId: string, buyer: Buyer, minorNotice: boolean): Promise<void> {
    // 원스토어 앱 안에서는 웹 PG 결제창을 열지 않는다(스토어 인앱결제 정책)
    if (isAppChannel()) { this.set({ kind: 'error', text: errorText('app-channel') }); return; }
    if (this.phase.kind === 'busy') return;
    this.set({ kind: 'busy', text: '주문을 만드는 중…' });
    const o = await api<{ paymentId: string; storeId: string; channelKey: string; orderName: string; totalAmount: number; customData: string }>('POST', '/api/iap/order', { productId, minorNotice, ...channelHint() });
    if (!o.ok) { this.set({ kind: 'error', text: errorText(o.code) }); return; }
    let sdk: PortoneSdk;
    try { sdk = await loadSdk(); } catch { this.set({ kind: 'error', text: '결제창을 불러오지 못했어요. 네트워크를 확인해 주세요.' }); return; }
    this.set({ kind: 'busy', text: '결제창에서 결제를 진행해 주세요…' });
    const origin = location.origin;
    let resp: Awaited<ReturnType<PortoneSdk['requestPayment']>>;
    try {
      resp = await sdk.requestPayment({
        storeId: o.data.storeId, channelKey: o.data.channelKey, paymentId: o.data.paymentId, orderName: o.data.orderName,
        totalAmount: o.data.totalAmount, currency: 'CURRENCY_KRW', payMethod: 'CARD', customData: o.data.customData,
        customer: { fullName: buyer.name.trim(), phoneNumber: buyer.phone.replace(/-/g, '').trim(), email: buyer.email.trim() },
        redirectUrl: `${origin}/?${RETURN_FLAG}=1`,
        // 결제별 웹훅 주소(같은 포트원 상점의 다른 제품 웹훅과 섞이지 않게)
        noticeUrls: [`${origin}/api/iap/webhook`],
      });
    } catch { this.set({ kind: 'error', text: errorText(null) }); return; }
    if (resp && resp.code !== undefined) {
      this.set({ kind: 'error', text: isUserCancel(resp.code, resp.message) ? errorText('user-cancel') : '결제가 실패했어요. 청구되지 않았어요.' + (resp.message ? ' (' + resp.message.slice(0, 80) + ')' : '') });
      return;
    }
    await this.confirm(o.data.paymentId);
  }

  /** Play 구매: 주문 → 구매 시트(PaymentRequest) → purchaseToken → 서버 확인(지급). 웹 포트원 경로와 별개 */
  async buyPlay(productId: string, minorNotice: boolean): Promise<void> {
    if (this.phase.kind === 'busy') return;
    if (this.mode() !== 'play') { this.set({ kind: 'error', text: errorText('app-channel') }); return; }
    this.set({ kind: 'busy', text: '주문을 만드는 중…' });
    const o = await api<{ orderId: string; sku: string; obfuscatedAccountId: string }>('POST', '/api/iap/play/order', { productId, minorNotice });
    if (!o.ok) { this.set({ kind: 'error', text: errorText(o.code) }); return; }
    savePending({ orderId: o.data.orderId, sku: o.data.sku });
    this.set({ kind: 'busy', text: 'Google Play 결제창에서 결제를 진행해 주세요…' });
    let token: string;
    try { token = await requestPlayPurchase(o.data.sku, o.data.obfuscatedAccountId); }
    catch (err) {
      savePending(null);
      this.set({ kind: 'error', text: isPlayCancel(err) ? PLAY_CANCEL_TEXT : '결제가 실패했어요. 청구되지 않았어요.' });
      return;
    }
    savePending({ orderId: o.data.orderId, sku: o.data.sku, token });
    await this.completePlay(o.data.orderId, token);
  }

  /** play/complete(멱등). pending이면 1.5·3·4.5·6·7.5초 간격으로 다시 묻고, 그래도 안 되면 자동 지급 안내 */
  async completePlay(orderId: string, token: string): Promise<void> {
    this.set({ kind: 'busy', text: '결제를 확인하는 중…' });
    for (let i = 0; i <= PLAY_PENDING_DELAYS.length; i++) {
      const r = await api<{ result: string; wallet: IapWalletView }>('POST', '/api/iap/play/complete', { orderId, purchaseToken: token });
      if (r.ok) {
        savePending(null);
        this.wallet = r.data.wallet;
        this.set({ kind: 'done', text: '결제 완료! 젬이 지급됐어요.' });
        await this.refresh();
        return;
      }
      if (playCompleteAction(r.code) === 'fail') { savePending(null); this.set({ kind: 'error', text: errorText(r.code) }); return; }
      if (i < PLAY_PENDING_DELAYS.length) await new Promise((res) => setTimeout(res, PLAY_PENDING_DELAYS[i]));
    }
    // 서버 RTDN·Cron이 같은 토큰으로 지급한다(저장해 둔 토큰은 다음 상점 열기에 한 번 더 시도)
    this.set({ kind: 'done', text: PLAY_PENDING_TEXT });
  }

  /** 같은 세션에서 토큰까지 받았는데 complete가 끝나지 않았으면 다시 시도(서버 멱등) */
  private async recoverPlay(): Promise<void> {
    if (this.phase.kind === 'busy' || this.mode() !== 'play') return;
    const p = loadPending();
    if (p?.token) await this.completePlay(p.orderId, p.token);
  }

  /**
   * 구매 복원: Digital Goods listPurchases()의 미소비 구매마다 POST /api/iap/play/restore를 한 번씩(앱을 켤 때·상점을 열 때). 성공하면 지갑 갱신 + 한 줄 안내.
   * 실패(404 대기 주문 없음 포함)는 조용히 — 서버 RTDN·Cron(maintainPlay)이 처리한다.
   */
  private async restorePlay(): Promise<void> {
    const svc = this.playSvc;
    if (!svc?.listPurchases || this.mode() !== 'play') return;
    let list: Awaited<ReturnType<NonNullable<DigitalGoodsService['listPurchases']>>>;
    try { list = await svc.listPurchases(); } catch { return; }
    for (const t of restoreTargets(list, this.restored)) {
      this.restored.add(t.purchaseToken);
      const r = await api<{ result: string; wallet: IapWalletView }>('POST', '/api/iap/play/restore', { sku: t.itemId, purchaseToken: t.purchaseToken });
      // 일시 실패(로그인 전·네트워크·서버·Play 장애·결제 대기·요청 제한)는 다음 기회(상점 열기·다음 실행)에 다시 시도한다
      if (!r.ok && restoreRetryable(r.status, r.code)) { this.restored.delete(t.purchaseToken); continue; }
      if (!r.ok || r.data.result !== 'paid') continue;
      const gained = restoreGained(this.wallet?.total, r.data.wallet?.total);
      this.wallet = r.data.wallet;
      if (gained && this.phase.kind !== 'busy') this.set({ kind: 'done', text: PLAY_RESTORED_TEXT });
      else this.bump();
      if (gained) void this.refresh(); // 구매 내역도 새로(복원할 토큰은 이미 시도했으므로 되돌지 않는다)
    }
  }

  /** 서버 확인: 결제 대기면 짧게 몇 번 다시 묻는다(웹훅이 따로 지급하므로 놓쳐도 다음에 상점을 열면 반영) */
  async confirm(paymentId: string): Promise<void> {
    this.set({ kind: 'busy', text: '결제를 확인하는 중…' });
    for (let i = 0; i < 5; i++) {
      const r = await api<{ wallet: IapWalletView }>('POST', '/api/iap/complete', { paymentId, ...channelHint() });
      if (r.ok) {
        this.wallet = r.data.wallet;
        this.set({ kind: 'done', text: '결제 완료! 젬이 지급됐어요.' });
        await this.refresh();
        return;
      }
      if (r.code !== 'payment-pending' && r.code !== 'portone-unavailable') { this.set({ kind: 'error', text: errorText(r.code) }); return; }
      await new Promise((res) => setTimeout(res, 1500 * (i + 1)));
    }
    this.set({ kind: 'error', text: errorText('payment-pending') });
  }

  /** 모바일 결제창 복귀: 주소의 결제 결과를 지우고(새로고침 반복 방지) 서버에 확인한다 */
  private async handleReturn(): Promise<void> {
    let q: URLSearchParams;
    try { q = new URLSearchParams(location.search); } catch { return; }
    if (q.get(RETURN_FLAG) !== '1') return;
    const paymentId = q.get('paymentId'), code = q.get('code') ?? undefined, message = q.get('message') ?? undefined;
    for (const k of [RETURN_FLAG, 'paymentId', 'code', 'message', 'transactionType', 'txId']) q.delete(k);
    try { history.replaceState(history.state, '', location.pathname + (q.toString() ? '?' + q.toString() : '') + location.hash); } catch { /* 주소 정리 실패는 무시 */ }
    if (!paymentId) return;
    if (code) { this.set({ kind: 'error', text: isUserCancel(code, message) ? errorText('user-cancel') : '결제가 실패했어요. 청구되지 않았어요.' }); return; }
    await this.confirm(paymentId);
  }

  async claimDaily(): Promise<void> {
    const r = await api<{ claimed: boolean; wallet: IapWalletView }>('POST', '/api/iap/claim', {});
    if (r.ok) { this.wallet = r.data.wallet; this.set({ kind: 'done', text: r.data.claimed ? '오늘의 월정액 젬을 받았어요.' : '오늘은 이미 받았어요.' }); }
    else this.set({ kind: 'error', text: errorText(r.code) });
  }

  async requestRefund(paymentId: string): Promise<void> {
    const r = await api<{ wallet: IapWalletView }>('POST', '/api/iap/refund-request', { paymentId });
    if (r.ok) { this.wallet = r.data.wallet; this.set({ kind: 'done', src: 'refund', text: '청약철회(환불) 요청을 접수했어요. 확인 후 3영업일 안에 결제 수단으로 취소돼요.' }); await this.refresh(); }
    else this.set({ kind: 'error', src: 'refund', text: r.code === 'not-eligible' ? '결제 후 7일이 지났거나 젬을 이미 사용해 청약철회할 수 없어요. 고객센터로 문의해 주세요.' : errorText(r.code) });
  }

  clearPhase(): void { if (this.phase.kind !== 'busy') this.set({ kind: 'idle' }); }
}

/** 앱 하나에 클라이언트 하나(상점 탭·설정 구매 내역이 같이 쓴다) */
let shared: IapClient | null = null;
export function iapClient(): IapClient {
  if (!shared) { shared = new IapClient(); void shared.init(); }
  return shared;
}
